/**
 * inventoryTurnover.ts — Pure computation of inventory turnover: how many times
 * stock "turned over" in the window, overall and per category, plus days of
 * inventory on hand. The financial-efficiency companion to Dead Stock (which
 * lists individual slow items); this answers "how hard is my capital working".
 *
 * Turnover = COGS ÷ inventory value, both valued at the current purchase price
 * (a consistent basis, and cost-gate-free — it doesn't need the `cost_price`
 * snapshot). Days of inventory = window days ÷ turnover. Sales are net units
 * over the window.
 *
 * Pure — the screen feeds it product rows (reusing the Dead Stock loader).
 */

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface TurnoverProductInput {
  id: string;
  name: string;
  category: string;
  currentStock: number;
  purchasePrice: number;
  /** Net units sold over the window. */
  unitsSold: number;
}

export interface InventoryTurnoverInput {
  products: TurnoverProductInput[];
  windowDays: number;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface TurnoverMetrics {
  cogs: number;
  inventoryValue: number;
  /** COGS ÷ inventory value; null when there is no inventory to turn. */
  turns: number | null;
  /** windowDays ÷ turns; null when turns is null or zero. */
  daysOfInventory: number | null;
}

export interface TurnoverProduct extends TurnoverMetrics {
  productId: string;
  name: string;
  category: string;
  currentStock: number;
  unitsSold: number;
}

export interface TurnoverCategory extends TurnoverMetrics {
  category: string;
}

export interface InventoryTurnover {
  overall: TurnoverMetrics;
  byCategory: TurnoverCategory[];
  /** Products with stock, fastest-turning first. */
  products: TurnoverProduct[];
  windowDays: number;
}

function metrics(cogs: number, inventoryValue: number, windowDays: number): TurnoverMetrics {
  const turns = inventoryValue > 0 ? cogs / inventoryValue : null;
  const daysOfInventory = turns && turns > 0 ? windowDays / turns : null;
  return {
    cogs: round2(cogs),
    inventoryValue: round2(inventoryValue),
    turns: turns != null ? Math.round(turns * 100) / 100 : null,
    daysOfInventory: daysOfInventory != null ? Math.round(daysOfInventory) : null,
  };
}

export function buildInventoryTurnover(input: InventoryTurnoverInput): InventoryTurnover {
  const { windowDays } = input;
  const products = input.products.filter((p) => p.currentStock > 0);

  let totalCogs = 0;
  let totalInv = 0;
  const catMap = new Map<string, { cogs: number; inv: number }>();

  const productRows: TurnoverProduct[] = products.map((p) => {
    const inventoryValue = p.currentStock * p.purchasePrice;
    const cogs = p.unitsSold * p.purchasePrice;
    totalCogs += cogs;
    totalInv += inventoryValue;
    const c = catMap.get(p.category) ?? { cogs: 0, inv: 0 };
    c.cogs += cogs;
    c.inv += inventoryValue;
    catMap.set(p.category, c);
    return {
      productId: p.id,
      name: p.name,
      category: p.category,
      currentStock: p.currentStock,
      unitsSold: round2(p.unitsSold),
      ...metrics(cogs, inventoryValue, windowDays),
    };
  });

  // Fastest turns first; a null-turn product (no inventory value) sinks to the end.
  productRows.sort((a, b) => (b.turns ?? -1) - (a.turns ?? -1));

  const byCategory: TurnoverCategory[] = [...catMap.entries()]
    .map(([category, v]) => ({ category, ...metrics(v.cogs, v.inv, windowDays) }))
    .sort((a, b) => (b.turns ?? -1) - (a.turns ?? -1));

  return {
    overall: metrics(totalCogs, totalInv, windowDays),
    byCategory,
    products: productRows,
    windowDays,
  };
}
